import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Package, CheckCircle2, ArrowRight, RefreshCw,
  Loader2, Clock, Layers, Wrench, ShieldCheck, Truck,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ordersApi } from '@/services/api';
import { toast } from 'sonner';
import OrderLayout from './OrderLayout';
import { useOrderState } from './useOrderState';
import { useLanguage } from '@/context/LanguageContext';

const STAGES = ['pending', 'materials_prep', 'manufacturing', 'quality_check', 'ready'];

const STAGE_ICONS = {
  pending: Clock,
  materials_prep: Layers,
  manufacturing: Wrench,
  quality_check: ShieldCheck,
  ready: Truck,
};

const STAGE_COLORS = {
  pending: { bg: 'bg-charcoal/6', border: 'border-charcoal/12', text: 'text-charcoal/40', icon: 'text-charcoal/30' },
  materials_prep: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: 'text-blue-500' },
  manufacturing: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: 'text-amber-500' },
  quality_check: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', icon: 'text-purple-500' },
  ready: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: 'text-green-500' },
};

const ProductionPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLang } = useLanguage();
  const { order, setOrder, localData, loading } = useOrderState();
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!loading && !localData) navigate('/start');
  }, [loading, localData, navigate]);

  const refresh = useCallback(async () => {
    if (!localData?.orderId) return;
    setRefreshing(true);
    try {
      const res = await ordersApi.getById(localData.orderId);
      setOrder(res.data);
    } catch {
      toast.error(t('order.error'));
    } finally {
      setRefreshing(false);
    }
  }, [localData, setOrder, t]);

  useEffect(() => {
    const interval = setInterval(refresh, 30000);
    return () => clearInterval(interval);
  }, [refresh]);

  if (loading) {
    return (
      <OrderLayout currentStep={5}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-gold" size={32} />
        </div>
      </OrderLayout>
    );
  }

  const production = order?.production || { status: 'pending' };
  const currentIdx = STAGES.indexOf(production.status);
  const isReady = production.status === 'ready';
  const progressPct = Math.round(((currentIdx + 1) / STAGES.length) * 100);

  const formatDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString(currentLang === 'ar' ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <OrderLayout currentStep={5} orderNumber={localData?.orderNumber}>
      <div className="space-y-8">
        {/* Header + refresh */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-charcoal/60 text-sm leading-relaxed max-w-xs">
              {t('order.production.desc')}
            </p>
            {production.updated_at && (
              <p className="text-charcoal/35 text-xs mt-2 flex items-center gap-1.5">
                <Clock size={11} />
                {t('order.production.lastUpdated')}: {formatDate(production.updated_at)}
              </p>
            )}
          </div>
          <button
            onClick={refresh}
            disabled={refreshing}
            title={t('order.production.refreshStatus')}
            className="flex items-center gap-2 text-xs font-medium text-charcoal/45 hover:text-gold transition-colors px-3 py-2 border border-charcoal/12 hover:border-gold/30 flex-shrink-0"
          >
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
            {t('order.production.refreshStatus')}
          </button>
        </div>

        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-charcoal/50 uppercase tracking-wider">
              {t('order.production.title')}
            </span>
            <span className="text-sm font-bold text-gold">{progressPct}%</span>
          </div>
          <div className="h-2.5 bg-charcoal/8 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-gold/70 to-gold rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </div>
        </div>

        {/* Stage cards */}
        <div className="space-y-3">
          {STAGES.map((stage, index) => {
            const isCompleted = index < currentIdx;
            const isActive = index === currentIdx;
            const isPending = index > currentIdx;
            const Icon = STAGE_ICONS[stage];
            const colors = isActive ? STAGE_COLORS[stage] : {};

            return (
              <motion.div
                key={stage}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.06 }}
                className={`flex items-center gap-4 p-4 border transition-all duration-300 ${
                  isCompleted
                    ? 'bg-gold/4 border-gold/15'
                    : isActive
                    ? `${colors.bg} ${colors.border} shadow-sm`
                    : 'bg-charcoal/2 border-charcoal/8'
                }`}
              >
                {/* Status icon */}
                <div
                  className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                    isCompleted
                      ? 'bg-gold'
                      : isActive
                      ? `${colors.bg} border-2 ${colors.border}`
                      : 'bg-charcoal/5 border border-charcoal/10'
                  }`}
                >
                  {isCompleted ? (
                    <CheckCircle2 size={20} className="text-white" />
                  ) : (
                    <Icon
                      size={18}
                      className={
                        isActive
                          ? `${colors.icon} ${isActive ? 'animate-pulse' : ''}`
                          : 'text-charcoal/20'
                      }
                    />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p
                    className={`font-semibold text-sm ${
                      isCompleted ? 'text-charcoal/60' : isActive ? colors.text : 'text-charcoal/25'
                    }`}
                  >
                    {t(`order.production.${stage}`)}
                  </p>
                  {isActive && production.notes && (
                    <p className="text-xs mt-0.5 text-charcoal/50 truncate">{production.notes}</p>
                  )}
                </div>

                {/* Badge */}
                <div className="flex-shrink-0">
                  {isCompleted && (
                    <span className="text-xs font-semibold text-gold/70 bg-gold/10 px-2 py-1">
                      ✓
                    </span>
                  )}
                  {isActive && (
                    <span className={`text-xs font-semibold px-2.5 py-1 ${colors.bg} ${colors.text} border ${colors.border}`}>
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-current me-1 animate-pulse align-middle" />
                      {t('order.dashboard.inProgress')}
                    </span>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Ready: installation CTA */}
        <AnimatePresence>
          {isReady && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              className="relative overflow-hidden border-2 border-green-300 bg-gradient-to-br from-green-50 to-emerald-50 p-6 text-center"
            >
              <div className="absolute -top-8 -end-8 w-32 h-32 rounded-full bg-green-200/30" />
              <div className="relative">
                <p className="text-2xl mb-3">🎉</p>
                <p className="font-bold text-green-800 text-lg mb-1">{t('order.production.readyMsg')}</p>
                <p className="text-green-700/70 text-sm mb-4">
                  {currentLang === 'ar' ? 'الخطوة الأخيرة — حدد موعد التركيب!' : 'Final step — schedule your installation!'}
                </p>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  animate={{ boxShadow: ['0 0 0 0 rgba(34,197,94,0)', '0 0 0 10px rgba(34,197,94,0.2)', '0 0 0 0 rgba(34,197,94,0)'] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  onClick={() => navigate('/installation')}
                  className="inline-flex items-center gap-3 bg-green-600 text-white font-bold px-8 py-4 hover:bg-green-700 transition-colors shadow-lg"
                >
                  {t('order.production.scheduleNow')} <ArrowRight size={18} />
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Waiting hint */}
        {!isReady && (
          <div className="text-center py-2">
            <p className="text-charcoal/35 text-xs">
              {currentLang === 'ar'
                ? 'يتم تحديث الحالة تلقائياً كل ٣٠ ثانية'
                : 'Status updates automatically every 30 seconds'}
            </p>
          </div>
        )}
      </div>
    </OrderLayout>
  );
};

export default ProductionPage;
